import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, MapPin, UserCheck, MessageSquare, Ban, Heart, Flag, ExternalLink } from "lucide-react";

// Neighbour-to-neighbour safety prompt (user request, 2026-07).
// Shown at the two commitment points of help requests: when a helper offers
// and when a requester accepts an offer. Acknowledgement is stored ONCE per
// device (localStorage help_safety_ack) so repeat actions aren't nagged —
// the permanent "Safety tips" link on help-request pages reopens the content
// any time. Org opportunities skip this (they carry the org-verification layer).
const ACK_KEY = "help_safety_ack";
export const hasSafetyAck = () => {
  try { return localStorage.getItem(ACK_KEY) === "1"; } catch { return true; }
};
const setSafetyAck = () => {
  try { localStorage.setItem(ACK_KEY, "1"); } catch {}
};

const TIPS = [
  { icon: MapPin, text: "Meet in a public place first when you can — a café, a library, a busy corner." },
  { icon: UserCheck, text: "Let someone you trust know where you're going and when." },
  { icon: MessageSquare, text: "Keep conversations on CivicHearth so there's always a record." },
  { icon: Ban, text: "Never share bank details or send money — genuine neighbours won't ask." },
  { icon: Heart, text: "Trust your instincts. It's always OK to decline or stop — no explanation needed." },
  { icon: Flag, text: "See something that doesn't feel right? Use the report button and we'll take a look." },
];
export const NEIGHBOUR_SAFETY_TIPS = TIPS; // reused by the public /safety page

function SafetyPageLink() {
  return (
    <Link
      to="/safety"
      data-testid="safety-full-guide-link"
      className="inline-flex items-center gap-1 text-xs text-forest/70 hover:text-forest underline underline-offset-2 transition-colors"
    >
      Read the full safety guide <ExternalLink className="w-3 h-3" />
    </Link>
  );
}

function TipsList() {
  return (
    <ul className="space-y-2.5 text-left" data-testid="safety-tips-list">
      {TIPS.map((t, i) => (
        <li key={t.text} className="flex items-start gap-2.5 text-sm text-neutral-700">
          <t.icon className="w-4 h-4 text-forest/70 mt-0.5 shrink-0" />
          <span>{t.text}</span>
        </li>
      ))}
    </ul>
  );
}

/** Confirm-mode dialog gating a commitment action (offer / accept).
 *  Tester fix (2026-07-09): explicit acknowledgement REQUIRED — the confirm
 *  button stays disabled until the checkbox is ticked, so the dialog can't be
 *  clicked or Enter-keyed through. The tick resets every time it opens. */
export function SafetyConfirmDialog({ open, onOpenChange, onConfirm, confirmLabel = "I understand — continue" }) {
  const [acked, setAcked] = useState(false);
  const handleOpenChange = (v) => {
    if (!v) setAcked(false);
    onOpenChange(v);
  };
  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent data-testid="safety-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-forest" /> A quick word on staying safe
          </AlertDialogTitle>
          <AlertDialogDescription>
            Neighbour-to-neighbour help is what CivicHearth is all about. A few good habits keep it safe and lovely for everyone:
          </AlertDialogDescription>
        </AlertDialogHeader>
        <TipsList />
        <SafetyPageLink />
        <label className="flex items-start gap-2.5 rounded-xl border border-forest/15 bg-forest/5 px-3.5 py-3 cursor-pointer select-none">
          <Checkbox
            data-testid="safety-ack-checkbox"
            checked={acked}
            onCheckedChange={(v) => setAcked(v === true)}
            className="mt-0.5"
          />
          <span className="text-sm text-forest font-medium">I've read these tips and understand them</span>
        </label>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="safety-cancel-btn">Not now</AlertDialogCancel>
          <AlertDialogAction
            data-testid="safety-confirm-btn"
            disabled={!acked}
            onClick={() => { if (!acked) return; setSafetyAck(); setAcked(false); onConfirm(); }}
            className="bg-forest hover:bg-forest-hover disabled:opacity-50 disabled:pointer-events-none"
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Permanent info link for help-request pages — same content, read-only. */
export function SafetyTipsLink({ className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="safety-tips-link"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-forest transition-colors ${className}`}
      >
        <ShieldCheck className="w-3.5 h-3.5" /> Safety tips for meeting neighbours
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="safety-info-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-forest" /> Staying safe, neighbour to neighbour
            </AlertDialogTitle>
          </AlertDialogHeader>
          <TipsList />
          <SafetyPageLink />
          <AlertDialogFooter>
            <AlertDialogAction data-testid="safety-info-close" className="bg-forest hover:bg-forest-hover">Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
